import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import StarRating from '@/components/StarRating';
import { format } from 'date-fns';
import { RecruiterReview } from '@/lib/types';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Define the type for raw data from Supabase
interface RecruiterReviewRaw {
  id: string;
  company: string;
  reviewer_name: string;
  agency: string;
  feedback: string;
  last_placement_date: string | null;
  company_rating: number;
  culture_rating: number;
  process_rating: number;
  department: string;
  created_at: string;
}

const RecruiterReviews = () => {
  const { companyName } = useParams<{ companyName: string }>();
  const [recruiterReviews, setRecruiterReviews] = useState<RecruiterReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const decodedCompanyName = decodeURIComponent(companyName || '');
  
  useEffect(() => {
    const fetchRecruiterReviews = async () => {
      try {
        // Use a type assertion to avoid TypeScript errors
        const response = await supabase
          .from('recruiter_reviews' as any)
          .select('*')
          .eq('company', decodedCompanyName)
          .order('created_at', { ascending: false });
          
        const { data, error } = response;

        if (error) {
          console.error('Error fetching recruiter reviews:', error);
          toast({
            title: 'Error',
            description: 'Failed to load recruiter reviews',
            variant: 'destructive'
          });
          return;
        }

        // Safely cast the data to our expected type
        const rawReviews = data as unknown as RecruiterReviewRaw[];
        
        // Transform Supabase data to match RecruiterReview type
        const formattedReviews: RecruiterReview[] = rawReviews.map(review => ({
          id: review.id,
          company: review.company,
          reviewerName: review.reviewer_name,
          agency: review.agency,
          feedback: review.feedback,
          lastPlacementDate: review.last_placement_date ? new Date(review.last_placement_date) : undefined,
          ratings: {
            company: review.company_rating,
            culture: review.culture_rating,
            process: review.process_rating
          },
          date: review.created_at
        }));

        setRecruiterReviews(formattedReviews);
      } catch (error) {
        console.error('Unexpected error:', error);
        toast({
          title: 'Error',
          description: 'An unexpected error occurred',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecruiterReviews();
  }, [decodedCompanyName, toast]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <Button 
            variant="ghost" 
            asChild
            className="mb-6 text-redflags-gray hover:text-redflags-dark"
          >
            <Link to={`/companies/${companyName}`} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to {decodedCompanyName} Profile
            </Link>
          </Button>
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-redflags-dark mb-2">Recruiter Reviews</h1>
            <p className="text-redflags-gray mb-6">
              Insights from recruitment consultants who have worked with {decodedCompanyName}
            </p>
            
            <Card className="border border-redflags-light/70 mb-6">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-600 mb-2">
                  Our recruiter reviews provide valuable insights into {decodedCompanyName}'s hiring patterns, 
                  interview processes, and overall stability from the perspective of industry recruitment consultants.
                </p>
                <p className="text-sm text-gray-600">
                  These reviews can help identify potential growth or contraction signals that may impact job security.
                </p>
              </CardContent>
            </Card>
            
            <div className="space-y-6">
              {isLoading ? (
                <div className="text-center py-10">
                  <p className="text-gray-500">Loading recruiter reviews...</p>
                </div>
              ) : recruiterReviews.length > 0 ? (
                recruiterReviews.map(review => (
                  <Card key={review.id} className="border border-redflags-light/70">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-medium text-lg text-redflags-dark">{review.reviewerName}</h3>
                          <p className="text-sm text-redflags-gray">{review.agency}</p>
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium">Company:</span>
                            <StarRating rating={review.ratings.company} size={16} />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium">Culture:</span>
                            <StarRating rating={review.ratings.culture} size={16} />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium">Process:</span>
                            <StarRating rating={review.ratings.process} size={16} />
                          </div>
                          <span className="text-xs text-redflags-gray">
                            {new Date(review.date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-redflags-dark mb-4">{review.feedback}</p>
                      
                      {review.lastPlacementDate && (
                        <div className="flex items-center justify-between pt-2 border-t border-redflags-light/30">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-redflags-gray" />
                            <span className="text-sm">
                              Last Placement Made: <span className="font-medium text-redflags-dark">
                                {format(review.lastPlacementDate, 'MMM dd, yyyy')}
                              </span>
                            </span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-10">
                  <p className="text-gray-500">No recruiter reviews available for this company yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RecruiterReviews;
